import { useEffect } from 'react';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import type { CalibrationViewSummary } from '../types';

export interface UseCalibrationDataOptions {
  selectedCampaignId: number | null;
  calibrationViews: CalibrationViewSummary[];
  setCalibrationViews: React.Dispatch<
    React.SetStateAction<CalibrationViewSummary[]>
  >;
  setAdminFilter: React.Dispatch<React.SetStateAction<ApiGroup | undefined>>;
}

export interface UseCalibrationDataReturn {
  isLoading: boolean;
  errorMessage: string | null;
}

/**
 * Custom hook for managing calibration dashboard data
 *
 * Handles:
 * - Data fetching and API integration
 * - Dashboard data preparation and transformation
 * - Relationship management and deduplication
 * - Loading and error states
 * - Data caching and comparison
 *
 * @param options - Configuration options for data management
 * @returns Dashboard data, relationships, loading state, and helper functions
 */
export const useCalibrationData = (
  options: UseCalibrationDataOptions
): UseCalibrationDataReturn => {
  const { data, error, status } = useConfirmApi<{
    list: CalibrationViewSummary[];
    admin_filter?: ApiGroup;
  }>({
    url: `/calibration-views`,
    params: {
      campaign_id: options.selectedCampaignId,
    },
    method: 'GET',
    disabled: options.calibrationViews.length > 0,
  });

  useEffect(() => {
    if (status === 'SUCCESS' && data && options.calibrationViews.length === 0) {
      options.setCalibrationViews(data.list);
      options.setAdminFilter(data.admin_filter);
    }
  }, [status, data, options]);

  const isLoading = status === 'LOADING';

  return {
    isLoading,
    errorMessage: error as string,
  };
};
